import * as THREE from 'three'
import { SessionManager } from '../xr/SessionManager'
import { NarratorLite } from '../map/shared/NarratorLite'
import { Subtitle } from '../ui/Subtitle'
import { SplatEnvironment } from '../scene/SplatEnvironment'

// ============================================================
// ワープホールMRデモ — 改訂シナリオの序〜破ミニ通し
// MR(パススルー)で武四郎+招待状 → 表門が突然出現 → ワープホールを
// くぐるとVR(泰山荘・書院前) → 崖線の山道をゆっくり下る → 一畳敷(3DGS)内部
// ============================================================

type Phase = 'idle' | 'title' | 'adjust' | 'gate' | 'grounds' | 'descend' | 'interior' | 'end'

const overlay = document.getElementById('overlay')!
const startAr = document.getElementById('startAr') as HTMLButtonElement
const startPc = document.getElementById('startPc') as HTMLButtonElement
const captionEl = document.getElementById('caption')!
const nextBtn = document.getElementById('next') as HTMLButtonElement
const phaseEl = document.getElementById('phase')!

const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.setSize(window.innerWidth, window.innerHeight)
renderer.xr.enabled = true
renderer.xr.setFoveation(1)
document.getElementById('app')!.appendChild(renderer.domElement)

const scene = new THREE.Scene()
const player = new THREE.Group()
scene.add(player)
const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.05, 300)
camera.position.set(0, 1.6, 0)
player.add(camera)

scene.add(new THREE.HemisphereLight(0xfff2dc, 0x33414d, 1.1))
const sun = new THREE.DirectionalLight(0xffe8c0, 1.4)
sun.position.set(5, 12, 4)
scene.add(sun)

const xrs = new SessionManager(renderer)
const subtitle = new Subtitle(camera)

// --- MRの代役(PC/VRフォールバック): 現実の部屋のプレースホルダ ---
const fakeRoom = new THREE.Group()
fakeRoom.add(new THREE.GridHelper(8, 16, 0x39424e, 0x242b34))
fakeRoom.visible = false
scene.add(fakeRoom)

// --- フラッシュ(カメラ子・ワープ演出) ---
const flashMat = new THREE.MeshBasicMaterial({
  color: 0xffffff, transparent: true, opacity: 0, depthTest: false, depthWrite: false,
})
const flash = new THREE.Mesh(new THREE.PlaneGeometry(6, 6), flashMat)
flash.position.z = -0.5
flash.renderOrder = 999
camera.add(flash)
function doFlash(v: number): void { flashMat.opacity = v }

// --- 和紙ラベルテクスチャ ---
function makeLabel(lines: string[], w = 256, h = 360): THREE.CanvasTexture {
  const c = document.createElement('canvas')
  c.width = w; c.height = h
  const g = c.getContext('2d')!
  g.fillStyle = '#f2ecdc'; g.fillRect(0, 0, w, h)
  g.strokeStyle = '#a53f2b'; g.lineWidth = 6; g.strokeRect(8, 8, w - 16, h - 16)
  g.fillStyle = '#33291c'
  g.font = `bold ${Math.floor(w / 5.4)}px "HGP行書体","HG行書体","Yu Mincho",serif`
  g.textAlign = 'center'
  lines.forEach((t, i) => g.fillText(t, w / 2, h * (0.32 + i * 0.26)))
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

// --- 招待状・勧進帳・木片(序で舞い降りる) ---
interface Falling { m: THREE.Object3D; y0: number; y1: number; delay: number; sway: number; t: number }
const papers = new THREE.Group()
scene.add(papers)
const falling: Falling[] = []
function spawnPapers(): void {
  const inv = new THREE.Mesh(
    new THREE.PlaneGeometry(0.24, 0.34),
    new THREE.MeshBasicMaterial({ map: makeLabel(['木片勧進', '招待状']), side: THREE.DoubleSide }),
  )
  inv.position.set(-0.35, 2.6, -1.1)
  papers.add(inv)
  falling.push({ m: inv, y0: 2.6, y1: 1.35, delay: 0.4, sway: 1.7, t: 0 })

  const ledger = new THREE.Mesh(
    new THREE.PlaneGeometry(0.3, 0.21),
    new THREE.MeshBasicMaterial({ map: makeLabel(['勧進帳'], 300, 210), side: THREE.DoubleSide }),
  )
  ledger.position.set(0.4, 2.8, -1.2)
  papers.add(ledger)
  falling.push({ m: ledger, y0: 2.8, y1: 1.15, delay: 1.1, sway: 2.3, t: 0 })

  const chipGeo = new THREE.BoxGeometry(0.05, 0.012, 0.03)
  const chipMat = new THREE.MeshStandardMaterial({ color: 0xb08a5a, roughness: 0.9 })
  for (let i = 0; i < 10; i++) {
    const chip = new THREE.Mesh(chipGeo, chipMat)
    const a = (i / 10) * Math.PI * 2
    chip.position.set(Math.cos(a) * (0.5 + 0.3 * Math.sin(i * 3)), 2.4 + (i % 4) * 0.25, -1.0 - Math.sin(a) * 0.5)
    papers.add(chip)
    falling.push({ m: chip, y0: chip.position.y, y1: 0.006, delay: 0.2 + i * 0.22, sway: 2 + i, t: 0 })
  }
  papers.visible = true
}

// --- タイトル(行書体・飛来) ---
function makeTitlePlane(): THREE.Mesh {
  const c = document.createElement('canvas')
  c.width = 1024
  c.height = 400
  const g = c.getContext('2d')!
  g.clearRect(0, 0, 1024, 400)
  g.textAlign = 'center'
  g.font = '900 168px "HGP行書体", "HG行書体", "HGS行書体", "Yu Mincho", serif'
  g.lineWidth = 16
  g.strokeStyle = 'rgba(40,20,4,0.9)'
  g.strokeText('空飛ぶ一畳敷', 512, 200)
  g.fillStyle = '#ffd77a'
  g.fillText('空飛ぶ一畳敷', 512, 200)
  g.font = '600 42px "HGP行書体", "HG行書体", "Yu Mincho", serif'
  g.fillStyle = '#f4e8c8'
  g.fillText('— 松浦武四郎と旅する、一畳の書斎 —', 512, 300)
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return new THREE.Mesh(
    new THREE.PlaneGeometry(2.3, 0.9),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, side: THREE.DoubleSide }),
  )
}
const title = makeTitlePlane()
title.visible = false
scene.add(title)

// --- MR位置調整ゴースト(表門の出現位置プレビュー) ---
const ghost = new THREE.Group()
{
  const mat = new THREE.LineBasicMaterial({ color: 0x7bd7ff, transparent: true, opacity: 0.85 })
  const frame = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(2.6, 3.2, 0.35)), mat)
  frame.position.y = 1.6
  ghost.add(frame)
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(1.5, 1.62, 40),
    new THREE.MeshBasicMaterial({ color: 0x7bd7ff, transparent: true, opacity: 0.4, side: THREE.DoubleSide, depthWrite: false }),
  )
  ring.rotation.x = -Math.PI / 2
  ring.position.y = 0.02
  ghost.add(ring)
}
ghost.visible = false
scene.add(ghost)

// --- 表門(薬医門風・プロシージャル) + ワープホール膜 ---
const gate = new THREE.Group()
gate.position.set(0, 0, -2.9)
gate.visible = false
scene.add(gate)
{
  const wood = new THREE.MeshStandardMaterial({ color: 0x6e4b2e, roughness: 0.82 })
  const woodDark = new THREE.MeshStandardMaterial({ color: 0x4c3520, roughness: 0.85 })
  const roofMat = new THREE.MeshStandardMaterial({ color: 0x3a3f45, roughness: 0.7 })
  const stone = new THREE.MeshStandardMaterial({ color: 0x8b8b84, roughness: 0.95 })
  const add = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number, rz = 0): void => {
    const m = new THREE.Mesh(geo, mat)
    m.position.set(x, y, z); m.rotation.z = rz
    gate.add(m)
  }
  for (const sx of [-1, 1]) {
    add(new THREE.BoxGeometry(0.2, 2.3, 0.2), wood, sx * 0.95, 1.15, 0)          // 本柱
    add(new THREE.BoxGeometry(0.3, 0.14, 0.3), stone, sx * 0.95, 0.07, 0)        // 礎石
    add(new THREE.BoxGeometry(0.14, 1.9, 0.14), woodDark, sx * 0.95, 0.95, 0.6)  // 控柱
    add(new THREE.BoxGeometry(0.1, 0.1, 0.66), woodDark, sx * 0.95, 1.7, 0.3)    // 繋ぎ貫
  }
  add(new THREE.BoxGeometry(2.6, 0.22, 0.26), wood, 0, 2.28, 0)                  // 冠木
  add(new THREE.BoxGeometry(2.9, 0.1, 0.2), woodDark, 0, 2.5, 0.15)              // 棟木受け
  const slope = 0.46
  add(new THREE.BoxGeometry(3.2, 0.07, 1.25), roofMat, 0, 2.86, -0.5, 0)         // 屋根前
  gate.children[gate.children.length - 1].rotation.x = slope
  add(new THREE.BoxGeometry(3.2, 0.07, 1.25), roofMat, 0, 2.86, 0.62)
  gate.children[gate.children.length - 1].rotation.x = -slope
  add(new THREE.BoxGeometry(3.25, 0.12, 0.18), roofMat, 0, 3.14, 0.05)           // 大棟
}

const membraneMat = new THREE.ShaderMaterial({
  transparent: true,
  depthWrite: false,
  side: THREE.DoubleSide,
  uniforms: { uT: { value: 0 }, uOpen: { value: 0 } },
  vertexShader: `varying vec2 vUv; void main(){ vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.); }`,
  // 異次元の裂け目: 奥へ吸い込まれる対数トンネル＋三本腕スパイラル＋明滅する星
  fragmentShader: `
    uniform float uT; uniform float uOpen; varying vec2 vUv;
    float hash(vec2 q){ return fract(sin(dot(q, vec2(12.9898,78.233)))*43758.5453); }
    void main(){
      vec2 p = vUv*2.-1.;
      float r = length(p);
      if(r>1.) discard;
      float a = atan(p.y,p.x);
      float t = uT;
      // 奥行き: 中心へ落ちる同心リング（時間で吸い込まれる）
      float depth = fract(-t*0.7 + 0.35/(r+0.12));
      float tunnel = smoothstep(0.0,0.22,depth)*smoothstep(1.0,0.5,depth);
      // 三本腕の渦（中心ほど速く巻く）
      float arm = sin(a*3.0 + t*2.8 - 2.4/(r+0.15));
      float armGlow = smoothstep(0.55,0.95,arm);
      vec3 deep   = vec3(0.04,0.06,0.22);
      vec3 violet = vec3(0.45,0.18,0.85);
      vec3 cyan   = vec3(0.30,0.80,1.00);
      vec3 gold   = vec3(1.00,0.85,0.50);
      vec3 col = mix(deep, violet, tunnel);
      col += cyan * armGlow * (1.0-r) * 0.9;
      col += violet * smoothstep(0.75,0.2,r) * 0.35;
      // 中心の白熱コア（脈動）
      col += mix(gold, vec3(1.0), 0.5) * smoothstep(0.30,0.0,r) * (0.7+0.3*sin(t*5.0));
      // 事象の地平線リム
      float rim = smoothstep(0.10,0.0,abs(r-0.93));
      col += gold * rim * 2.4;
      // 明滅する微光星
      float sp = hash(floor(p*44.0 + t*9.0));
      col += vec3(1.0) * step(0.995, sp) * 0.9;
      float alpha = (smoothstep(1.0,0.12,r)*0.96 + rim) * uOpen;
      gl_FragColor = vec4(col, alpha);
    }`,
})
const membrane = new THREE.Mesh(new THREE.CircleGeometry(0.86, 48), membraneMat)
membrane.position.set(0, 1.25, 0)
gate.add(membrane)

// 吸い込まれる粒子（渦の外周から中心へ螺旋落下・金とシアンの二重らせん）
const SUCK_N = 320
const suckGeo = new THREE.BufferGeometry()
suckGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(SUCK_N * 3), 3))
const suckCol = new Float32Array(SUCK_N * 3)
for (let i = 0; i < SUCK_N; i++) {
  const c = i % 2 === 0 ? [1.0, 0.82, 0.5] : [0.4, 0.85, 1.0]
  suckCol.set(c, i * 3)
}
suckGeo.setAttribute('color', new THREE.BufferAttribute(suckCol, 3))
const suckMat = new THREE.PointsMaterial({
  size: 0.045, vertexColors: true, transparent: true, opacity: 0,
  blending: THREE.AdditiveBlending, depthWrite: false,
})
const suck = new THREE.Points(suckGeo, suckMat)
suck.frustumCulled = false
gate.add(suck)
const suckSeed = Array.from({ length: SUCK_N }, (_, i) => ({
  ang: (i / SUCK_N) * Math.PI * 2 * 7,
  speed: 0.16 + (i % 13) * 0.014,
  phase: (i * 0.618) % 1,
  wob: (i % 7) * 0.9,
}))
function updateSuck(t: number): void {
  const pos = suckGeo.getAttribute('position') as THREE.BufferAttribute
  for (let i = 0; i < SUCK_N; i++) {
    const s = suckSeed[i]
    const life = (t * s.speed + s.phase) % 1 // 0=外周 → 1=中心
    const rad = (1.0 - life) * 1.7 + 0.04
    const ang = s.ang + life * 8.5 + t * 0.25
    pos.setXYZ(i,
      Math.cos(ang) * rad,
      1.25 + Math.sin(ang) * rad * 0.85,
      Math.sin(t * 1.7 + s.wob) * 0.16 * (1 - life))
  }
  pos.needsUpdate = true
  suckMat.opacity = (membraneMat.uniforms.uOpen.value as number) * 0.95
}

// --- 出現バースト ---
let burst: THREE.Points | null = null
let burstVel: Float32Array | null = null
let burstLife = 0
function spawnBurst(at: THREE.Vector3): void {
  const n = 90
  const pos = new Float32Array(n * 3)
  burstVel = new Float32Array(n * 3)
  for (let i = 0; i < n; i++) {
    pos.set([at.x, at.y + 1.3, at.z], i * 3)
    const th = Math.random() * Math.PI * 2
    const ph = Math.acos(2 * Math.random() - 1)
    const sp = 1.2 + Math.random() * 2.4
    burstVel.set([Math.sin(ph) * Math.cos(th) * sp, Math.cos(ph) * sp, Math.sin(ph) * Math.sin(th) * sp], i * 3)
  }
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3))
  burst = new THREE.Points(geo, new THREE.PointsMaterial({
    color: 0xffd77a, size: 0.05, transparent: true, opacity: 1, blending: THREE.AdditiveBlending, depthWrite: false,
  }))
  scene.add(burst)
  burstLife = 1.2
}

// --- VR世界(泰山荘・書院前 + 崖線の下り道) ---
const SKY = new THREE.Color(0xa7c4dd)
const INTERIOR_BG = new THREE.Color(0x17120c)
const worldFog = new THREE.Fog(SKY, 20, 90)
const world = new THREE.Group()
world.visible = false
scene.add(world)

{
  const plateau = new THREE.Mesh(
    new THREE.CircleGeometry(15, 40),
    new THREE.MeshStandardMaterial({ color: 0x2d3a2c, roughness: 1 }),
  )
  plateau.rotation.x = -Math.PI / 2
  plateau.position.set(0, -0.02, -6)
  world.add(plateau)

  // 書院(CGスタンドイン)
  const shoin = new THREE.Group()
  const woodMat = new THREE.MeshStandardMaterial({ color: 0x5d4128, roughness: 0.85 })
  const wallMat = new THREE.MeshStandardMaterial({ color: 0xece2cb, roughness: 0.9 })
  const roofMat = new THREE.MeshStandardMaterial({ color: 0x474c52, roughness: 0.75 })
  const sBase = new THREE.Mesh(new THREE.BoxGeometry(5.4, 0.5, 3.6), woodMat)
  sBase.position.y = 0.25
  const sWall = new THREE.Mesh(new THREE.BoxGeometry(5.0, 1.7, 3.2), wallMat)
  sWall.position.y = 1.35
  const sRoof = new THREE.Mesh(new THREE.BoxGeometry(6.2, 0.16, 4.4), roofMat)
  sRoof.position.y = 2.35
  const sRoof2 = new THREE.Mesh(new THREE.BoxGeometry(4.4, 0.14, 3.0), roofMat)
  sRoof2.position.y = 2.72
  const engawa = new THREE.Mesh(new THREE.BoxGeometry(5.8, 0.1, 0.9), woodMat)
  engawa.position.set(0, 0.5, 2.1)
  shoin.add(sBase, sWall, sRoof, sRoof2, engawa)
  for (let i = 0; i < 6; i++) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.1, 1.8, 0.1), woodMat)
    post.position.set(-2.4 + i * 0.96, 1.35, 1.62)
    shoin.add(post)
  }
  shoin.position.set(-3.6, 0, -9.5)
  shoin.rotation.y = 0.5
  world.add(shoin)
}

// 下り道(崖線): スプライン + リボン + 木々
const path = new THREE.CatmullRomCurve3([
  new THREE.Vector3(0, 0, -4),
  new THREE.Vector3(0.4, 0, -10),
  new THREE.Vector3(1.8, -1.2, -16),
  new THREE.Vector3(-1.6, -2.8, -24),
  new THREE.Vector3(1.2, -4.2, -31),
  new THREE.Vector3(0, -5.4, -38),
])
{
  const seg = 64
  const half = 1.15
  const verts: number[] = []
  const idx: number[] = []
  const up = new THREE.Vector3(0, 1, 0)
  for (let i = 0; i <= seg; i++) {
    const t = i / seg
    const p = path.getPointAt(t)
    const tan = path.getTangentAt(t)
    const side = new THREE.Vector3().crossVectors(tan, up).normalize()
    verts.push(p.x - side.x * half, p.y + 0.01, p.z - side.z * half)
    verts.push(p.x + side.x * half, p.y + 0.01, p.z + side.z * half)
    if (i < seg) {
      const b = i * 2
      idx.push(b, b + 1, b + 2, b + 1, b + 3, b + 2)
    }
  }
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3))
  geo.setIndex(idx)
  geo.computeVertexNormals()
  world.add(new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: 0x7a6a4f, roughness: 1, side: THREE.DoubleSide })))

  const clearing = new THREE.Mesh(
    new THREE.CircleGeometry(7, 32),
    new THREE.MeshStandardMaterial({ color: 0x2a352a, roughness: 1 }),
  )
  clearing.rotation.x = -Math.PI / 2
  clearing.position.set(0, -5.42, -38)
  world.add(clearing)

  const trunkMat = new THREE.MeshStandardMaterial({ color: 0x4a3826, roughness: 0.95 })
  const leafMat = new THREE.MeshStandardMaterial({ color: 0x27442b, roughness: 0.95 })
  for (let i = 0; i < 26; i++) {
    const t = (i % 13) / 13
    const p = path.getPointAt(t)
    const side = i < 13 ? -1 : 1
    const tree = new THREE.Group()
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.14, 1.6, 6), trunkMat)
    trunk.position.y = 0.8
    const c1 = new THREE.Mesh(new THREE.ConeGeometry(0.9 + (i % 3) * 0.25, 2.2, 7), leafMat)
    c1.position.y = 2.4
    const c2 = new THREE.Mesh(new THREE.ConeGeometry(0.65 + (i % 3) * 0.2, 1.6, 7), leafMat)
    c2.position.y = 3.4
    tree.add(trunk, c1, c2)
    const spread = 2.4 + (i * 37 % 10) * 0.34
    tree.position.set(p.x + side * spread, p.y - 0.35, p.z + ((i * 13) % 5) * 0.5 - 1)
    tree.scale.setScalar(0.8 + (i * 7 % 5) * 0.14)
    world.add(tree)
  }
}

// --- 一畳敷 3DGS ---
const env = new SplatEnvironment()
scene.add(env.root)
let splatLoading: Promise<void> | null = null
function preloadSplat(): void {
  splatLoading ??= env.load('./assets/scenes.json').catch((e) => console.warn('[portal] splat load失敗', e))
}

// --- 武四郎 ---
const narrator = new NarratorLite()
scene.add(narrator.root)
narrator.root.visible = false
void narrator.load('./assets/takeshiro_rigged.glb')

// ============================================================
// 進行
// ============================================================
let phase: Phase = 'idle'
let phaseT = 0
let popT = 0
let dollyFrom: THREE.Vector3 | null = null
let dollyTo: THREE.Vector3 | null = null
let dollyT = 0
let descendT = 0
const DESCEND_DUR = 32

interface Line { at: number; text: string; dur: number; done?: boolean }
let lines: Line[] = []
function say(text: string, dur: number): void {
  captionEl.textContent = text
  captionEl.classList.add('show')
  if (renderer.xr.isPresenting) subtitle.show(text, dur)
  window.clearTimeout(sayTimer)
  sayTimer = window.setTimeout(() => captionEl.classList.remove('show'), dur * 1000)
}
let sayTimer = 0

const PHASE_LABELS: Record<Phase, string> = {
  idle: '', title: 'タイトル', adjust: 'MRシステム — 位置調整', gate: '表門(MR)とワープホール',
  grounds: '泰山荘・書院前(VR)', descend: '破 — 崖線の山道を下る', interior: '一畳敷(3DGS)', end: 'おわり',
}

const camWorldTmp = new THREE.Vector3()
const fwdTmp = new THREE.Vector3()
let whitePending = false // ワープのホワイトアウト進行中

function setPhase(p: Phase): void {
  phase = p
  phaseT = 0
  popT = 0
  lines = []
  phaseEl.textContent = PHASE_LABELS[p]
  switch (p) {
    case 'title':
      fakeRoom.visible = xrs.mode !== 'ar'
      title.visible = true
      ;(title.material as THREE.MeshBasicMaterial).opacity = 1
      break
    case 'adjust':
      title.visible = false
      ghost.visible = true
      lines = [
        { at: 0.2, text: '【位置調整】表門の出現位置を決めます。正面の枠に位置を合わせ、トリガー／▶で確定', dur: 60 },
      ]
      break
    case 'gate': {
      // ゴーストの位置・向きで表門を実体化
      ghost.visible = false
      gate.position.copy(ghost.position)
      gate.rotation.y = ghost.rotation.y
      gate.visible = true
      spawnBurst(gate.position)
      // 武四郎と招待状は門の脇に現れる
      narrator.root.position.copy(gate.localToWorld(new THREE.Vector3(1.7, 0, 0.9)))
      narrator.root.position.y = 0
      narrator.root.rotation.y = gate.rotation.y + Math.PI + 0.35
      narrator.root.scale.setScalar(0.05)
      narrator.root.visible = true
      camera.getWorldPosition(camWorldTmp)
      papers.position.set(camWorldTmp.x, 0, camWorldTmp.z)
      spawnPapers()
      lines = [
        { at: 1.0, text: '泰山荘の表門じゃ。門の内に、時の渦が見えるであろう', dur: 6 },
        { at: 7.5, text: '空から舞い降りるは、木片の招待状と勧進帳', dur: 6 },
        { at: 14, text: 'さあ、ワープホールをくぐってみよ（歩いて通るか、トリガー／▶で進む）', dur: 9 },
      ]
      break
    }
    case 'grounds':
      scene.background = SKY
      scene.fog = worldFog
      world.visible = true
      fakeRoom.visible = false
      papers.visible = false
      narrator.root.position.set(-1.6, 0, -7.2)
      narrator.root.rotation.y = Math.PI - 0.3
      preloadSplat()
      lines = [
        { at: 0.8, text: 'ここは泰山荘。三鷹・国際基督教大学の森に、七つの棟が今も残る', dur: 7 },
        { at: 8.2, text: 'あれが書院。泰山荘の母屋よ。国の登録有形文化財じゃ', dur: 7 },
        { at: 15.6, text: 'さあ、崖線(がいせん)の山道を、ゆるりと下ろう。わしの書斎はその先じゃ', dur: 7.5 },
      ]
      break
    case 'descend':
      descendT = 0
      lines = [
        { at: 3, text: 'ゆっくりとな。……わしは十六で家を出て、六十余州を歩き尽くした', dur: 8 },
        { at: 13, text: '北の大地に「北海道」と名を付けたのも、その旅の果てのことよ', dur: 8 },
        { at: 24, text: '見えてきたな。谷あいのあれが——わしの一畳敷じゃ', dur: 7 },
      ]
      break
    case 'interior': {
      doFlash(0.8)
      env.showStage('interior')
      env.root.position.copy(player.position)
      scene.background = INTERIOR_BG
      scene.fog = new THREE.Fog(INTERIOR_BG, 6, 18)
      narrator.root.position.set(player.position.x - 0.75, player.position.y, player.position.z - 1.15)
      narrator.root.rotation.y = Math.PI + 0.3
      lines = [
        { at: 0.8, text: '一畳敷——一畳きりの、わしの書斎じゃ。柱の一本、板の一枚まで、全国の古き社寺の材よ', dur: 9 },
        { at: 10.4, text: '光る木片に触れれば、その木の物語が立ち上がる……それは本篇でのお楽しみ', dur: 8 },
        { at: 19, text: '聞きたいことは、マイクでわしに問うがよい。AIのわしが答える——これも本篇でな', dur: 9 },
      ]
      break
    }
    case 'end':
      lines = [{ at: 0.3, text: 'デモはここまで。本篇は二人ひと組、同じ畳の上で。——またこの一畳を訪ねてまいれ', dur: 12 }]
      nextBtn.textContent = '⟲ 最初から'
      break
    default:
      break
  }
}

function advance(): void {
  switch (phase) {
    case 'title': setPhase('adjust'); break
    case 'adjust': setPhase('gate'); break
    case 'gate':
      if (!dollyTo) {
        dollyFrom = player.position.clone()
        dollyTo = gate.localToWorld(new THREE.Vector3(0, 0, -1.7))
        dollyTo.y = 0
        dollyT = 0
      }
      break
    case 'grounds': setPhase('descend'); break
    case 'descend': descendT = Math.max(descendT, DESCEND_DUR - 1.2); break
    case 'interior': setPhase('end'); break
    case 'end': window.location.reload(); break
    default: break
  }
}
nextBtn.addEventListener('click', advance)
window.addEventListener('keydown', (e) => { if (e.code === 'Space') advance() })
for (const i of [0, 1]) {
  const c = renderer.xr.getController(i)
  c.addEventListener('select', advance)
  player.add(c)
}

// PCプレビュー: ドラッグ見回し
let dragging = false
let px = 0, py = 0, pitch = 0
renderer.domElement.addEventListener('pointerdown', (e) => { dragging = true; px = e.clientX; py = e.clientY })
window.addEventListener('pointerup', () => { dragging = false })
window.addEventListener('pointermove', (e) => {
  if (!dragging || renderer.xr.isPresenting) return
  player.rotation.y -= (e.clientX - px) * 0.004
  pitch = THREE.MathUtils.clamp(pitch - (e.clientY - py) * 0.004, -1.2, 1.2)
  camera.rotation.x = pitch
  px = e.clientX; py = e.clientY
})

// --- 開始 ---
async function start(preferAr: boolean): Promise<void> {
  overlay.style.display = 'none'
  if (preferAr) await xrs.enter(true)
  setPhase('title')
}
startAr.addEventListener('click', () => void start(true))
startPc.addEventListener('click', () => void start(false))
void xrs.detect().then((s) => { if (!s.ar && !s.vr) startAr.disabled = true })
xrs.onEnd = () => window.location.reload()

// ============================================================
// ループ
// ============================================================
const clock = new THREE.Clock()
const camWorld = new THREE.Vector3()
renderer.setAnimationLoop(() => {
  const dt = Math.min(clock.getDelta(), 0.1)
  phaseT += dt
  membraneMat.uniforms.uT.value += dt
  membrane.rotation.z += dt * 0.5
  if (gate.visible) updateSuck(membraneMat.uniforms.uT.value as number)
  narrator.update(dt)
  subtitle.update(dt)
  if (!whitePending && flashMat.opacity > 0) flashMat.opacity = Math.max(0, flashMat.opacity - dt * 0.9)

  for (const l of lines) {
    if (!l.done && phaseT >= l.at) { l.done = true; say(l.text, l.dur) }
  }

  // 舞い降り
  for (const f of falling) {
    if (!papers.visible) break
    f.t += dt
    const u = THREE.MathUtils.clamp((f.t - f.delay) / 2.6, 0, 1)
    const e = 1 - Math.pow(1 - u, 2)
    f.m.position.y = f.y0 + (f.y1 - f.y0) * e
    f.m.rotation.y += dt * f.sway * 0.6
    if (u < 1) f.m.position.x += Math.sin(f.t * f.sway) * dt * 0.12
    else if (f.m.position.y > 1) f.m.position.y = f.y1 + Math.sin(f.t * 1.4) * 0.03 // 着地後の浮遊
  }

  // バースト
  if (burst && burstVel) {
    burstLife -= dt
    const pos = burst.geometry.getAttribute('position') as THREE.BufferAttribute
    for (let i = 0; i < pos.count; i++) {
      pos.setXYZ(i,
        pos.getX(i) + burstVel[i * 3] * dt,
        pos.getY(i) + burstVel[i * 3 + 1] * dt,
        pos.getZ(i) + burstVel[i * 3 + 2] * dt)
      burstVel[i * 3 + 1] -= dt * 2.2
    }
    pos.needsUpdate = true
    ;(burst.material as THREE.PointsMaterial).opacity = Math.max(0, burstLife / 1.2)
    if (burstLife <= 0) { scene.remove(burst); burst.geometry.dispose(); burst = null; burstVel = null }
  }

  switch (phase) {
    case 'title': {
      // タイトルが遠くから飛来 → 静止 → 消えて位置調整へ
      const u = Math.min(1, phaseT / 1.4)
      const e = 1 - Math.pow(1 - u, 3)
      camera.getWorldPosition(camWorldTmp)
      title.position.set(
        camWorldTmp.x,
        1.55 + (1 - e) * 0.6 + Math.sin(phaseT * 1.4) * 0.03,
        camWorldTmp.z - (9 - 6.6 * e),
      )
      title.scale.setScalar(0.35 + 0.65 * e)
      title.lookAt(camWorldTmp.x, title.position.y, camWorldTmp.z)
      if (phaseT > 3.6) {
        const m = title.material as THREE.MeshBasicMaterial
        m.opacity = Math.max(0, 1 - (phaseT - 3.6) / 0.8)
        if (m.opacity <= 0) setPhase('adjust')
      }
      break
    }
    case 'adjust': {
      // ゴーストがユーザーの正面に追従（向きも正対）。トリガー/▶で確定
      camera.getWorldPosition(camWorldTmp)
      camera.getWorldDirection(fwdTmp)
      fwdTmp.y = 0
      if (fwdTmp.lengthSq() > 1e-4) fwdTmp.normalize()
      ghost.position.set(camWorldTmp.x + fwdTmp.x * 2.9, 0, camWorldTmp.z + fwdTmp.z * 2.9)
      ghost.rotation.y = Math.atan2(-fwdTmp.x, -fwdTmp.z)
      const pulse = 0.55 + 0.35 * Math.sin(phaseT * 4)
      ghost.traverse((o) => {
        const m = (o as THREE.Mesh).material as THREE.Material | undefined
        if (m && 'opacity' in m) (m as THREE.LineBasicMaterial).opacity = pulse
      })
      break
    }
    case 'gate': {
      if (narrator.loaded && narrator.root.visible) {
        narrator.root.scale.setScalar(Math.min(1, narrator.root.scale.x + dt * 1.4))
      }
      popT = Math.min(1, popT + dt * 2.2)
      const s = 1 - Math.pow(1 - popT, 3)
      gate.scale.set(s, 0.02 + 0.98 * s, s)
      membraneMat.uniforms.uOpen.value = THREE.MathUtils.clamp((phaseT - 0.8) / 1.2, 0, 1)
      if (dollyFrom && dollyTo) {
        dollyT = Math.min(1, dollyT + dt / 2.4)
        const e = dollyT < 0.5 ? 2 * dollyT * dollyT : 1 - Math.pow(-2 * dollyT + 2, 2) / 2
        player.position.lerpVectors(dollyFrom, dollyTo, e)
      }
      // 通過判定は門ローカル座標で（配置が自由になったため）
      camera.getWorldPosition(camWorld)
      const gLocal = gate.worldToLocal(camWorld.clone())
      if (!whitePending && gLocal.z < -0.06 && Math.abs(gLocal.x) < 1.5 && gLocal.y < 3.4) {
        whitePending = true // ホワイトアウト開始（白100%で世界を切替）
        // 白へ吸い込まれる
      }
      if (whitePending) {
        flashMat.opacity = Math.min(1, flashMat.opacity + dt * 2.2)
        if (flashMat.opacity >= 1) {
          whitePending = false
          dollyFrom = dollyTo = null
          setPhase('grounds')
        }
      }
      break
    }
    case 'grounds':
      membraneMat.uniforms.uOpen.value = Math.max(0, membraneMat.uniforms.uOpen.value - dt * 0.7)
      if (phaseT > 25) setPhase('descend')
      break
    case 'descend': {
      descendT += dt
      const t = THREE.MathUtils.clamp(descendT / DESCEND_DUR, 0, 1)
      const e = t < 0.06 ? t / 0.06 * t : t // 出だしだけ緩加速
      const p = path.getPointAt(THREE.MathUtils.clamp(e, 0, 1))
      player.position.set(p.x, p.y, p.z)
      if (t >= 1) setPhase('interior')
      break
    }
    default:
      break
  }

  renderer.render(scene, camera)
})

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(window.innerWidth, window.innerHeight)
})
